import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { PageHeader, GlassCard, PrimaryButton, GhostButton } from "@/components/Primitives";
import Modal, { inputCls, Field } from "@/components/Modal";
import { Plus, X, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

const blank = { client_id: "", plan_name: "Basic Care", monthly_fee: 0, included_hours: 4, hours_used: 0, renewal_date: "", status: "active" };

function MaintDialog({ open, initial, clients, onClose, onSaved }) {
  const [form, setForm] = useState(blank);
  useEffect(() => {
    if (open) setForm(initial ? { ...blank, ...initial, renewal_date: (initial.renewal_date || "").slice(0, 10) } : blank);
  }, [open, initial]);

  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthly_fee: Number(form.monthly_fee) || 0,
        included_hours: Number(form.included_hours) || 0,
        hours_used: Number(form.hours_used) || 0,
        renewal_date: form.renewal_date || null,
      };
      if (isEdit) await api.patch(`/maintenance/${initial.id}`, payload);
      else await api.post("/maintenance", payload);
      toast.success(isEdit ? "Saved" : "Created");
      onClose(); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const confirm = useConfirm();
  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this maintenance plan?",
      description: `${initial?.plan_name || "This plan"} will stop counting toward MRR.`,
      confirmLabel: "Delete plan",
    });
    if (!ok) return;
    try { await api.delete(`/maintenance/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <form onSubmit={submit} className="p-8" data-testid="maintenance-dialog">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-light">{isEdit ? "Edit plan" : "New maintenance plan"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Client">
            <select required className={inputCls} value={form.client_id} onChange={u("client_id")}>
              <option value="">— Select —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Field>
          <Field label="Plan Name"><input className={inputCls} value={form.plan_name} onChange={u("plan_name")} /></Field>
          <Field label="Monthly Fee"><input type="number" className={inputCls} value={form.monthly_fee} onChange={u("monthly_fee")} /></Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={u("status")}>
              {["active", "paused", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Included Hours"><input type="number" step="0.5" className={inputCls} value={form.included_hours} onChange={u("included_hours")} /></Field>
          <Field label="Hours Used"><input type="number" step="0.1" className={inputCls} value={form.hours_used} onChange={u("hours_used")} /></Field>
          <Field label="Renewal Date"><input type="date" className={inputCls} value={form.renewal_date} onChange={u("renewal_date")} /></Field>
        </div>
        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 text-sm inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit">{isEdit ? "Save" : "Create"}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [m, c] = await Promise.all([api.get("/maintenance"), api.get("/clients")]);
    setItems(m.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const mrr = items.reduce((s, m) => s + (m.monthly_fee || 0), 0);

  return (
    <div data-testid="maintenance-page">
      <PageHeader
        title="Maintenance"
        subtitle="Care plans, retained hours, and recurring revenue."
        testId="maintenance-header"
        action={<PrimaryButton onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-maintenance-btn"><Plus className="w-4 h-4" /> New Plan</PrimaryButton>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Maintenance MRR</div><div className="font-display text-3xl">{fmtMoney(mrr)}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Active Plans</div><div className="font-display text-3xl">{items.filter((m) => m.status === "active").length}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Hours This Month</div><div className="font-display text-3xl">{items.reduce((s, m) => s + (m.hours_used || 0), 0).toFixed(1)}</div></GlassCard>
      </div>

      <GlassCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                <th className="py-3 pr-4">Plan</th>
                <th className="py-3 pr-4">Client</th>
                <th className="py-3 pr-4">Monthly Fee</th>
                <th className="py-3 pr-4">Hours</th>
                <th className="py-3 pr-4">Used</th>
                <th className="py-3 pr-4">Renewal</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.05]">
              {items.map((m) => {
                const ratio = m.included_hours ? Math.min(100, (m.hours_used / m.included_hours) * 100) : 0;
                return (
                  <tr key={m.id} className="hover:bg-white/[0.03]">
                    <td className="py-3 pr-4 text-white">{m.plan_name}</td>
                    <td className="py-3 pr-4 text-white/60">{m.client_name}</td>
                    <td className="py-3 pr-4 text-white">{fmtMoney(m.monthly_fee)}</td>
                    <td className="py-3 pr-4 text-white/60">{m.included_hours}h</td>
                    <td className="py-3 pr-4">
                      <div className="w-32">
                        <div className="flex justify-between text-[10px] text-white/40 mb-1">
                          <span>{(m.hours_used || 0).toFixed(1)}h</span>
                          <span>{Math.round(ratio)}%</span>
                        </div>
                        <div className="h-1 bg-white/[0.05] rounded-full overflow-hidden">
                          <div className={`h-full ${ratio > 80 ? "bg-amber-400" : "bg-[#007AFF]"}`} style={{ width: `${ratio}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-white/60">{fmtDate(m.renewal_date)}</td>
                    <td className="py-3 pr-4">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-300 border border-emerald-400/20">{m.status}</span>
                    </td>
                    <td className="py-3 pr-4 text-right">
                      <button onClick={() => { setEditing(m); setOpen(true); }} className="text-white/55 hover:text-white p-1.5" data-testid={`maintenance-edit-${m.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-white/40">No maintenance plans yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </GlassCard>

      <MaintDialog open={open} initial={editing} clients={clients} onClose={() => { setOpen(false); setEditing(null); }} onSaved={load} />
    </div>
  );
}
